/* eslint-disable max-len */
/* eslint-disable max-params */
/* eslint-disable new-cap */
import { backsplashData } from './backsplash.data';
import { BlueprintInstanceType } from '../shared.enums';
import { KitchenDesignOptions } from '../kitchen-design-base';
import { Panel } from './panel.class';
import { PanelType } from './enums';
import { Publish } from '../../decorators';
import { ReferenceMap } from '../shared.types';
import { calculateBacksplashDimensions } from './helpers';
import type { Countertop } from '../countertops/countertop.class';

import type { BacksplashDataIn, BacksplashDataOut, BacksplashHorizontalAlign, BacksplashPairingSide } from './types';

/**
 * Type for the parent parameter used in Backsplash factory method.
 * This accepts any object that can be used as a parent in KitchenDesignOptions.
 * We use object type to allow any object including Panels which extends KitchenDesignBase.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
type BacksplashParentType = object;

export class Backsplash extends Panel {
	/**
	 * Factory method to create a new Backsplash for a specific island panel.
	 * This method handles all the complex logic for sizing, positioning, and accounting for
	 * adjacent backsplashes at corners.
	 *
	 * @param islandPanelId The ID of the island panel to create a backsplash for
	 * @param countertop The countertop that will own this backsplash
	 * @param options Optional configuration (height, etc.)
	 * @returns The created Backsplash, or null if creation failed
	 */
	@Publish('backsplash', 'created')
	static createForPanel (
		islandPanelId: string,
		countertop: Countertop,
		parent: BacksplashParentType,
		options: { height?: number } = {}
	): Backsplash | null {
		const dimensions = calculateBacksplashDimensions(islandPanelId, countertop);
		if (!dimensions) return null;

		const backsplashHeight = options.height ?? 4;
		const backsplashDepth = 1.25;

		const backsplashPanel = new Backsplash({
			dataIn: {
				type: PanelType.Back,
				label: `backsplash-${islandPanelId}`,
				associatedPanelId: islandPanelId,
				countertopOverhang: 0,
				centerOffset: dimensions.centerOffset,
				leftAdjoiningDepth: dimensions.leftAdjoiningDepth,
				rightAdjoiningDepth: dimensions.rightAdjoiningDepth,
				size: {
					width: dimensions.width,
					height: backsplashHeight,
					depth: backsplashDepth
				}
			},
			parent
		});

		return backsplashPanel;
	}
	#associatedPanelId: string | null = null;
	#centerOffset: number = 0;
	#pairedBacksplashId: string | null = null;
	#pairingSide: BacksplashPairingSide = null;
	#leftAdjoiningDepth: number = 0;
	#rightAdjoiningDepth: number = 0;
	#widthOverride: number | null = null;
	#horizontalAlign: BacksplashHorizontalAlign = 'left';
	#manualOffset: number = 0;

	constructor (options: KitchenDesignOptions<BacksplashDataIn> = {}) {
		super(options);

		const { dataIn = null } = options;
		const data = backsplashData(dataIn);

		this.#associatedPanelId = data.associatedPanelId ?? null;
		this.#centerOffset = data.centerOffset ?? 0;
		this.#pairedBacksplashId = data.pairedBacksplashId ?? null;
		this.#pairingSide = data.pairingSide ?? null;
		this.#leftAdjoiningDepth = data.leftAdjoiningDepth ?? 0;
		this.#rightAdjoiningDepth = data.rightAdjoiningDepth ?? 0;
		this.#widthOverride = data.widthOverride ?? null;
		this.#horizontalAlign = data.horizontalAlign ?? 'left';
		this.#manualOffset = data.manualOffset ?? 0;

		Object.defineProperty(this, 'instanceType', {
			value: BlueprintInstanceType.Backsplash,
			writable: false,
			configurable: false
		});

		this._refMap = {
			...this._refMap,
			type: BlueprintInstanceType.Backsplash,
			backsplash: this
		};

		this.setInitialized();
	}

	/**
	 * The ID of the panel this backsplash is associated with (sits behind)
	 * @returns string | null
	 */
	get associatedPanelId (): string | null {
		return this.#associatedPanelId;
	}

	/**
	 * Set the associated panel ID for this backsplash
	 */
	set associatedPanelId (panelId: string | null) {
		this.#associatedPanelId = panelId;
	}

	/**
	 * The center offset for positioning the backsplash along the panel width direction.
	 * This accounts for asymmetric overhangs and corner backsplash interactions.
	 * @returns number (default 0)
	 */
	get centerOffset (): number {
		return this.#centerOffset;
	}

	/**
	 * Set the center offset
	 */
	set centerOffset (offset: number) {
		this.#centerOffset = offset;
	}

	/**
	 * The ID of the backsplash this one is paired with
	 * @returns string | null
	 */
	get pairedBacksplashId (): string | null {
		return this.#pairedBacksplashId;
	}

	/**
	 * Set the paired backsplash ID
	 */
	@Publish('backsplash-pairing', 'updated')
	set pairedBacksplashId (backsplashId: string | null) {
		this.#pairedBacksplashId = backsplashId;
	}

	/**
	 * The side of the pair this backsplash is on ('left' or 'right')
	 * @returns BacksplashPairingSide
	 */
	get pairingSide (): BacksplashPairingSide {
		return this.#pairingSide;
	}

	/**
	 * Set the pairing side
	 */
	@Publish('backsplash-pairing', 'updated')
	set pairingSide (side: BacksplashPairingSide) {
		this.#pairingSide = side;
	}

	/**
	 * The depth subtracted for left adjacent backsplash at creation time.
	 * Used to maintain consistent corner adjustments during updates.
	 * @returns number (default 0)
	 */
	get leftAdjoiningDepth (): number {
		return this.#leftAdjoiningDepth;
	}

	/**
	 * The depth subtracted for right adjacent backsplash at creation time.
	 * Used to maintain consistent corner adjustments during updates.
	 * @returns number (default 0)
	 */
	get rightAdjoiningDepth (): number {
		return this.#rightAdjoiningDepth;
	}

	/**
	 * Manual width override - null means use calculated width
	 * @returns number | null
	 */
	get widthOverride (): number | null {
		return this.#widthOverride;
	}

	/**
	 * Set manual width override
	 */
	@Publish('backsplash', 'updated')
	set widthOverride (width: number | null) {
		this.#widthOverride = width;
		this.#recalculatePosition();
	}

	/**
	 * Horizontal alignment for positioning
	 * - 'left': Align to left edge (accounts for adjoining backsplash if present)
	 * - 'center': Center on available space
	 * - 'right': Align to right edge (accounts for adjoining backsplash if present)
	 * @returns BacksplashHorizontalAlign (default 'left')
	 */
	get horizontalAlign (): BacksplashHorizontalAlign {
		return this.#horizontalAlign;
	}

	/**
	 * Set horizontal alignment
	 */
	@Publish('backsplash', 'updated')
	set horizontalAlign (align: BacksplashHorizontalAlign) {
		this.#horizontalAlign = align;
		this.#recalculatePosition();
	}

	/**
	 * Manual offset from aligned position (no bounds)
	 * @returns number (default 0)
	 */
	get manualOffset (): number {
		return this.#manualOffset;
	}

	/**
	 * Set manual offset
	 */
	@Publish('backsplash', 'updated')
	set manualOffset (offset: number) {
		this.#manualOffset = offset;
		this.#recalculatePosition();
	}

	/**
	 * Recalculates the centerOffset based on current alignment and manual offset settings.
	 * Called when widthOverride, horizontalAlign, or manualOffset changes.
	 */
	#recalculatePosition (): void {
		const countertop = this.refMap?.countertop;
		if (countertop) {
			this.updateDimensions(countertop);
		}
	}

	/**
	 * Check if this backsplash is paired with another
	 * @returns boolean
	 */
	get isPaired (): boolean {
		return this.#pairedBacksplashId !== null;
	}

	/**
	 * Get the angle of the associated panel (determines which axis the backsplash faces)
	 * @returns number - angle in degrees (0, 90, 180, 270 typically)
	 */
	public getAssociatedPanelAngle (): number {
		const associatedPanel = this.refMap?.panels?.findById(this.#associatedPanelId);
		return associatedPanel?.getOriginAngle() ?? 0;
	}

	/**
	 * Check if this backsplash can be paired with another backsplash
	 * Two backsplashes can be paired if:
	 * 1. They face the same axis (angles differ by 0 or 180 degrees)
	 * 2. Their countertops are at the same global elevation (island heights match)
	 * @param other The other backsplash to check
	 * @returns boolean
	 */
	public canPairWith (other: Backsplash): boolean {
		if (!other || other.id === this.id) return false;
		if (this.isPaired || other.isPaired) return false;

		const thisAngle = this.getAssociatedPanelAngle();
		const otherAngle = other.getAssociatedPanelAngle();
		const angleDiff = Math.abs((((thisAngle - otherAngle) % 360) + 360) % 360);
		const sameAxis = angleDiff < 1 || Math.abs(angleDiff - 180) < 1;

		if (!sameAxis) return false;

		// Find the islands that own each backsplash's associated panel
		const thisIsland = this.#findIslandForPanel(this.#associatedPanelId);
		const otherIsland = other.#findIslandForPanel(other.associatedPanelId);

		if (!thisIsland || !otherIsland) return false;

		// Compare global countertop surface elevations (origin.y + island height)
		const heightTolerance = 0.5;
		const thisElevation = (thisIsland.origin?.y ?? 0) + (thisIsland.size?.height ?? 0);
		const otherElevation = (otherIsland.origin?.y ?? 0) + (otherIsland.size?.height ?? 0);

		return Math.abs(thisElevation - otherElevation) < heightTolerance;
	}

	/**
	 * Find the island that contains the specified panel
	 * @param panelId The panel ID to search for
	 * @returns The island containing the panel, or null if not found
	 */
	#findIslandForPanel (panelId: string | null): { origin?: { y?: number }, size?: { height?: number } } | null {
		if (!panelId) return null;

		const islands = this.refMap?.islands?.children ?? this.refMap?.kitchen?.design?.islands?.children;
		if (!islands) return null;

		for (const island of islands) {
			const panelExists = island.panels?.children?.some((panel: Panel) => panel.id === panelId);
			if (panelExists) {
				return island;
			}
		}

		return null;
	}

	/**
	 * Pair this backsplash with another, setting appropriate sides
	 * @param other The backsplash to pair with
	 * @param thisSide The side this backsplash should be on ('left' or 'right')
	 */
	public pairWith (other: Backsplash, thisSide: 'left' | 'right'): void {
		if (!this.canPairWith(other)) return;

		const otherSide = thisSide === 'left' ? 'right' : 'left';

		this.#pairedBacksplashId = other.id;
		this.#pairingSide = thisSide;

		other.pairedBacksplashId = this.id;
		other.pairingSide = otherSide;
	}

	/**
	 * Updates this backsplash's dimensions and position based on the current countertop state.
	 * This should be called when overhangs or island size changes.
	 *
	 * Uses stored adjoining depths to maintain consistent corner adjustments -
	 * this prevents backsplashes from incorrectly accounting for adjacent backsplashes
	 * that were created after them.
	 *
	 * Respects widthOverride, horizontalAlign, and manualOffset settings.
	 *
	 * @param countertop The countertop that owns this backsplash
	 */
	@Publish('backsplash', 'updated')
	public updateDimensions (countertop: Countertop): void {
		if (!this.#associatedPanelId) return;

		// Pass stored adjoining depths to maintain consistent corner adjustments
		const dimensions = calculateBacksplashDimensions(this.#associatedPanelId, countertop, {
			storedLeftAdjoiningDepth: this.#leftAdjoiningDepth,
			storedRightAdjoiningDepth: this.#rightAdjoiningDepth
		});
		if (!dimensions) return;

		// Use widthOverride if set, otherwise use calculated width
		const availableWidth = dimensions.width;
		const backsplashWidth = this.#widthOverride ?? availableWidth;

		// Calculate base offset from alignment
		// dimensions.centerOffset is the center of the available space
		// We need to adjust based on alignment and backsplash width
		let alignedOffset: number;
		const halfAvailable = availableWidth / 2;
		const halfBacksplash = backsplashWidth / 2;

		switch (this.#horizontalAlign) {
			case 'left':
				// Left edge of backsplash at left edge of available space
				// Center position = left edge + half backsplash width
				alignedOffset = dimensions.centerOffset - halfAvailable + halfBacksplash;
				break;
			case 'right':
				// Right edge of backsplash at right edge of available space
				// Center position = right edge - half backsplash width
				alignedOffset = dimensions.centerOffset + halfAvailable - halfBacksplash;
				break;
			case 'center':
			default:
				// Center of backsplash at center of available space
				alignedOffset = dimensions.centerOffset;
				break;
		}

		// Add manual offset (no bounds)
		this.#centerOffset = alignedOffset + this.#manualOffset;

		this.size = {
			width: backsplashWidth,
			height: this.size.height,
			depth: this.size.depth
		};
	}

	/**
	 * Unpair this backsplash from its paired backsplash
	 */
	@Publish('backsplash-pairing', 'updated')
	public unpair (): void {
		if (!this.#pairedBacksplashId) return;

		const countertop = this.refMap?.countertop;
		if (countertop) {
			const other = countertop.backsplashes?.children.find((b) => b.id === this.#pairedBacksplashId) as Backsplash | undefined;

			if (other) {
				other.pairedBacksplashId = null;
				other.pairingSide = null;
			}
		}

		this.#pairedBacksplashId = null;
		this.#pairingSide = null;
	}

	/**
	 * Serialize the backsplash data
	 * @returns BacksplashDataOut
	 */
	public override serialize (): BacksplashDataOut {
		const panelData = super.serialize();

		return {
			...panelData,
			associatedPanelId: this.#associatedPanelId,
			centerOffset: this.#centerOffset,
			pairedBacksplashId: this.#pairedBacksplashId,
			pairingSide: this.#pairingSide,
			leftAdjoiningDepth: this.#leftAdjoiningDepth,
			rightAdjoiningDepth: this.#rightAdjoiningDepth,
			widthOverride: this.#widthOverride,
			horizontalAlign: this.#horizontalAlign,
			manualOffset: this.#manualOffset
		};
	}
}
